import deepMerge from 'lodash.merge';

export type CombinedItem<Item, Collection> = Item & { _collection?: Collection };

export type CombinePaginatedApiBaseParams<AdditionalParams = unknown> = {
  limit: number;
  offset: number;
} & AdditionalParams;

export type QueryFnReturnType<Item, Collection, Metadata> = {
  total: number;
  items: Array<Item>;
  collection: Collection;
  metadata?: Partial<Metadata>;
};

export type QueryFn<Item, Collection, Metadata = unknown> = (
  params: CombinePaginatedApiBaseParams,
) => Promise<QueryFnReturnType<Item, Collection, Metadata>>;

type Props<Item, Collection, Metadata> = {
  queryFns: Array<QueryFn<Item, Collection, Metadata>>;
};

type GetDataFromQueriesProps<Item, Collection, Metadata> = {
  queries: Array<{
    total: number;
    queryFn: QueryFn<Item, Collection, Metadata>;
  }>;
  offset: number;
  limit: number;
};

export const getCumulatedTotal = ({ totals, index }: { totals: number[]; index: number }) =>
  totals.reduce((acc, curr, i) => (i < index ? acc + curr : acc), 0);

export const getDataFromQueries = <Item, Collection, Metadata>({
  queries,
  offset,
  limit,
  ...params
}: GetDataFromQueriesProps<Item, Collection, Metadata>): Array<
  Promise<QueryFnReturnType<Item, Collection, Metadata>>
> => {
  const totals = queries.map(({ total }) => total);
  let remainingLimit = limit;
  let currentOffset = offset;

  return queries
    .map(({ total, queryFn }, index) => {
      const startIndex = getCumulatedTotal({ totals, index });
      const endIndex = getCumulatedTotal({ totals, index: index + 1 });

      // Skip if we've already used up all items from this query
      if (currentOffset >= endIndex) {
        return null;
      }

      // Calculate how many items we need from this query
      const queryOffset = Math.max(0, currentOffset - startIndex);
      const remainingItemsInQuery = total - queryOffset;
      const itemsToFetch = Math.min(remainingLimit, remainingItemsInQuery);

      if (itemsToFetch <= 0 || queryFn == null) {
        return null;
      }

      // Update for next query
      currentOffset = endIndex;
      remainingLimit -= itemsToFetch;

      return queryFn({
        ...params,
        limit: itemsToFetch,
        offset: queryOffset,
      });
    })
    .filter((item): item is Promise<QueryFnReturnType<Item, Collection, Metadata>> =>
      Boolean(item),
    );
};

export const makeCombinedPaginatedQuery = <
  Item,
  Collection,
  Metadata = unknown,
  AdditionalParams = unknown,
>() => {
  const totals: Map<string, number[]> = new Map();
  const total: Map<string, number> = new Map();
  const metadata: Map<string, Partial<Metadata>> = new Map();

  return ({ queryFns }: Props<Item, Collection, Metadata>) =>
    async ({
      params,
      queryKeyHash,
    }: {
      params: CombinePaginatedApiBaseParams<AdditionalParams>;
      queryKeyHash: string;
    }): Promise<{
      total: number;
      items: Array<CombinedItem<Item, Collection>>;
      metadata?: Partial<Metadata>;
    }> => {
      const { offset } = params;

      if (offset === 0) {
        const res = await Promise.all(
          queryFns.map((query) => query({ ...params, offset: 0, limit: 1 })),
        );

        res.forEach((res) => {
          metadata.set(queryKeyHash, deepMerge({}, metadata.get(queryKeyHash), res.metadata ?? {}));
        });

        totals.set(
          queryKeyHash,
          res.map((r) => r.total),
        );
        total.set(
          queryKeyHash,
          totals.get(queryKeyHash)?.reduce((acc, curr) => acc + curr, 0) ?? 0,
        );
      }

      const queries = queryFns.map((queryFn, i) => ({
        queryFn,
        total: totals.get(queryKeyHash)?.[i] ?? 0,
      }));
      const data = Promise.all(getDataFromQueries({ queries, ...params }));

      return (await data).reduce<{
        total: number;
        items: Array<CombinedItem<Item, Collection>>;
        metadata?: Partial<Metadata>;
      }>(
        (acc, curr) => ({
          ...acc,
          items: [
            ...acc.items,
            ...curr.items.map((item) => ({
              ...item,
              _collection: curr.collection,
            })),
          ],
        }),
        { items: [], total: total.get(queryKeyHash) ?? 0, metadata: metadata.get(queryKeyHash) },
      );
    };
};

export type AppSpec = {
  app: 'web-citizen' | 'web-notary' | 'mobile-citizen' | 'web-citizen-portal';
  version: string;
} & (
  | { mobileOs: 'ios' | 'android'; mobileOsVersion: string }
  | { mobileOs?: never; mobileOsVersion?: never }
);

export const getIzimiMetadataHeaders = ({ appSpec }: { appSpec: AppSpec }) => ({
  'Izimi-App': appSpec.app,
  'Izimi-Version': appSpec.version,
  ...(appSpec.mobileOs
    ? {
        'Izimi-Mobile-Os': appSpec.mobileOs,
        'Izimi-Mobile-Os-Version': appSpec.mobileOsVersion,
      }
    : {}),
});
